import { Injectable } from '@angular/core';
import { InteractionStatus } from '@azure/msal-browser';
import { merge, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, mapTo, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { LogoutRequest } from '@nimbus-library/features/authentication/models/authentication-request.model';
import { IAuthenticationStorage } from '@nimbus-library/features/authentication/services/i-authentication-storage';
import { IApiToken, IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { MsalAuthenticationService } from '@nimbus-library/features/authentication/services/msal-authentication.service';
import { TeamsAuthenticationService } from '@nimbus-library/features/authentication/services/teams-authentication.service';
import { IUserInfo } from '@nimbus-library/features/menu/models/i-user.info';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';

@Injectable()
export class HybridAuthenticationService extends IAuthenticationService {
  private authenticationInstance: IAuthenticationService = this.teamsAuthenticationService;

  public status$: Observable<InteractionStatus> = merge(this.teamsAuthenticationService.status$, this.msalAuthentication.status$);

  public get isAuthenticated(): boolean {
    return this.isAuthenticated$.value;
  }

  public teamsCheckCompleted$: Observable<void>;

  public constructor(
    private readonly msalAuthentication: MsalAuthenticationService,
    private readonly teamsAuthenticationService: TeamsAuthenticationService,
    private readonly teamsContext: IMicrosoftTeamsContextAccessor,
    recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
    authenticationStorage: IAuthenticationStorage,
  ) {
    super(recurringTaskRunnerFactory, authenticationStorage);

    this.teamsCheckCompleted$ = this.teamsContext.getClientInfo$().pipe(
      take(1),
      mergeMap(() => {
        if (this.runningInTeams()) {
          return this.teamsAuthenticationService.aquireIdToken$().pipe(
            take(1),
            tap(() => {
              console.log('Teams SSO can be used stay on Teams Authentication Service.');
            }),
          );
        }

        return throwError('No teams client found');
      }),
      catchError((err) => {
        this.authenticationInstance = this.msalAuthentication;
        console.log('Teams SSO cannot be used fallback to MSAL Authentication Service.');

        return of(undefined);
      }),
      mapTo(undefined),
      shareReplay(1),
    );

    this.teamsCheckCompleted$
      .pipe(
        switchMap(() => this.authenticationInstance.isAuthenticated$),
        distinctUntilChanged(),
        filter((res) => !!res),
      )
      .subscribe((res) => {
        if (res) {
          this.isAuthenticated$.next(res);
        }
      });
  }

  public aquireIdToken$(loginHint?: string): Observable<{ token: string; expiration: moment.Moment }> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.aquireIdToken$(loginHint)),
    );
  }

  public getApiToken$(forceRefresh: boolean, scopes: string[]): Observable<IApiToken> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.getApiToken$(forceRefresh, scopes)),
    );
  }

  public getRawApiToken$(forceRefresh: boolean, scopes: string[]): Observable<any> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.getRawApiToken$(forceRefresh, scopes)),
    );
  }

  public getUserInfo(): IUserInfo {
    return this.authenticationInstance.getUserInfo();
  }

  public grantExplicitConsent$(scopes: string[], redirectUrl: string) {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.msalAuthentication.grantExplicitConsent$(scopes, redirectUrl)),
    );
  }

  public login(): Observable<void> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.login()),
    );
  }

  public logout(destroyServerSession: boolean, logoutRequest: LogoutRequest, reload: boolean = true): void {
    this.authenticationInstance.logout(destroyServerSession, logoutRequest, reload);
    this.isAuthenticated$.next(false);
    this.logout$.next();
  }

  public sso(loginHint: string): Observable<boolean> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.sso(loginHint)),
    );
  }

  public trackAuthentication(): Observable<void> {
    return this.teamsCheckCompleted$.pipe(
      take(1),
      switchMap(() => this.authenticationInstance.trackAuthentication()),
    );
  }
}
